
import React, { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCountry } from '@/contexts/country'; // Updated import path
import { supabase } from '@/integrations/supabase/client';

interface Constituency {
  id: string;
  constituency_name: string;
  constituency_code: string;
}

interface CardLocationProps {
  countryCode: string | null;
  onCountryChange: (value: string | null) => void;
  constituencyCode: string | null;
  onConstituencyChange: (value: string | null) => void;
}

const CardLocation: React.FC<CardLocationProps> = ({
  countryCode,
  onCountryChange,
  constituencyCode,
  onConstituencyChange
}) => {
  const { availableCountries } = useCountry();
  const [constituencies, setConstituencies] = useState<Constituency[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch constituencies when country changes
  useEffect(() => {
    if (!countryCode) {
      setConstituencies([]);
      if (constituencyCode) {
        onConstituencyChange(null);
      }
      return;
    }

    const fetchConstituencies = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('active_constituencies')
          .select('id, constituency_name, constituency_code')
          .eq('country_code', countryCode)
          .order('constituency_name', { ascending: true });

        if (error) throw error;
        setConstituencies(data || []);

        // If current constituency is not valid for the selected country, reset it
        if (constituencyCode) {
          const isValid = data?.some(c => c.constituency_code === constituencyCode);
          if (!isValid) {
            onConstituencyChange(null);
          }
        }
      } catch (error) {
        console.error('Error fetching constituencies:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchConstituencies();
  }, [countryCode, constituencyCode, onConstituencyChange]);

  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="grid gap-2">
        <Label htmlFor="country">Country</Label>
        <Select
          value={countryCode || undefined}
          onValueChange={(value) => 
            onCountryChange(value === 'global' ? null : value)
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Select country" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="global">Global (All Countries)</SelectItem>
            {availableCountries.filter(c => c.active).map((country) => (
              <SelectItem key={country.code} value={country.code}>
                {country.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <div className="grid gap-2">
        <Label htmlFor="constituency">Constituency (Optional)</Label>
        <Select
          value={constituencyCode || undefined}
          onValueChange={(value) => onConstituencyChange(value)}
          disabled={!countryCode || isLoading || constituencies.length === 0}
        >
          <SelectTrigger>
            <SelectValue placeholder={isLoading ? "Loading..." : constituencies.length === 0 ? "No constituencies available" : "Select constituency"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all_constituencies">All Constituencies</SelectItem>
            {constituencies.map((constituency) => (
              <SelectItem key={constituency.constituency_code} value={constituency.constituency_code}>
                {constituency.constituency_name} ({constituency.constituency_code})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default CardLocation;
